import { useClearErrors } from "@42.nl/react-error-store";
import { useEnum } from "@42.nl/react-spring-enums";
import { Button, JarbInput, JarbSelect, SubmitButton } from "@42.nl/ui";
import { Field, Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useTranslation } from "react-i18next";
import CheckboxField from "../components/Checkbox/CheckboxField";
import { InfoTooltip } from "../components/InfoTooltip/InfoTooltip";
import TextInput from "../i18n/TextInput";
import ReferenceTypeSelect from "../references/ReferenceTypeSelect";
import { PRODUCT_TYPES } from "../types/Product";

export type FilterFormData = {
  id?: number;
  entityType: string;
  name: string;
  sequence?: number;
  values?: string;
  exact: boolean;
  visible: boolean;
  valueType: string;
  valueSubType?: string;
  displayType: string;
  maxDisplayed?: number;
};

type Props = {
  initialValues: FilterFormData;
  onCancel: () => void;
  onSubmit: (data: FilterFormData) => void;
};

export default function FilterForm(props: Readonly<Props>) {
  const { onCancel, onSubmit, initialValues } = props;

  const displayTypes = useEnum("FilterDisplayType");
  const valueTypes = useEnum("ValueType");

  // Clear previous back-end errors if this form was shown before
  useClearErrors();
  const { t } = useTranslation(["translation", "filter"]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      mutators={{
        setValueSubType: (args, state, utils) => {
          utils.changeValue(state, "valueSubType", () => args.value);
        }
      }}
      render={({ handleSubmit, submitting, form }) => (
        <form onSubmit={handleSubmit}>
          <JarbInput
            id="name"
            name="name"
            type="text"
            jarb={{
              validator: "Filter.name",
              label: t("filter:COLUMNS.NAME")
            }}
            label={
              <>
                <span>{t("filter:COLUMNS.NAME")}*</span>
                <InfoTooltip tooltip={t("filter:NAME_TOOLTIP")} />
              </>
            }
          />

          <JarbSelect
            id="entityType"
            name="entityType"
            label={t("filter:COLUMNS.ENTITY_TYPE")}
            placeholder={t("filter:ENTITY_TYPE_PLACEHOLDER")}
            options={PRODUCT_TYPES}
            labelForOption={(type) => type}
            jarb={{
              validator: "Filter.entityType",
              label: t("filter:COLUMNS.ENTITY_TYPE")
            }}
          />

          <JarbSelect
            id="valueType"
            name="valueType"
            label={t("filter:COLUMNS.VALUE_TYPE")}
            options={valueTypes}
            labelForOption={(valueType) => t(`filter:VALUE_TYPES.${valueType}`)}
            jarb={{
              validator: "Filter.valueType",
              label: t("filter:COLUMNS.VALUE_TYPE")
            }}
          />

          <OnChange name="valueType">
            {() => {
              form.mutators.setValueSubType({ value: undefined });
            }}
          </OnChange>

          <Field name="valueType" subscription={{ value: true }}>
            {(fieldProps) => (
              <SubTypeSelect
                id="valueSubType"
                valueType={fieldProps.input.value}
              />
            )}
          </Field>

          <TextInput id="labels" label={t("filter:COLUMNS.LABEL")} />

          <TextInput id="tooltips" label={t("filter:COLUMNS.TOOLTIP")} />

          <JarbInput
            id="sequence"
            name="sequence"
            type="number"
            jarb={{
              validator: "Filter.sequence",
              label: t("filter:COLUMNS.SEQUENCE")
            }}
            label={t("filter:COLUMNS.SEQUENCE")}
          />

          <JarbInput
            id="values"
            name="values"
            jarb={{
              validator: "Filter.values",
              label: t("filter:COLUMNS.VALUES")
            }}
            label={t("filter:COLUMNS.VALUES")}
          />

          <JarbSelect
            id="displayType"
            name="displayType"
            label={t("filter:COLUMNS.DISPLAY_TYPE")}
            options={displayTypes}
            labelForOption={(displayType) =>
              t(`filter:DISPLAY_TYPES.${displayType}`)
            }
            jarb={{
              validator: "Filter.displayType",
              label: t("filter:COLUMNS.DISPLAY_TYPE")
            }}
          />

          <JarbInput
            id="maxDisplayed"
            name="maxDisplayed"
            type="number"
            jarb={{
              validator: "Filter.maxDisplayed",
              label: t("filter:COLUMNS.MAX_DISPLAYED")
            }}
            label={t("filter:COLUMNS.MAX_DISPLAYED")}
          />

          <CheckboxField
            name="exact"
            className="mb-3"
            label={t("filter:COLUMNS.EXACT")}
          />

          <CheckboxField
            name="visible"
            className="mb-3"
            label={t("filter:COLUMNS.VISIBLE")}
          />

          <div className="mb-2 d-flex gap-2 justify-content-end">
            <Button onClick={onCancel} color="secondary">
              {t("translation:CANCEL")}
            </Button>
            <SubmitButton inProgress={submitting}>
              {t("translation:SUBMIT_BUTTON")}
            </SubmitButton>
          </div>
        </form>
      )}
    />
  );
}

type SubTypeSelectProps = {
  id: string;
  valueType: string;
};

function SubTypeSelect({ id, valueType }: SubTypeSelectProps) {
  const { t } = useTranslation("filter");

  switch (valueType) {
    case "REFERENCE":
      return (
        <ReferenceTypeSelect
          id={id}
          label={t("COLUMNS.VALUE_SUB_TYPE")}
          placeholder={t("VALUE_SUB_TYPE_PLACEHOLDER")}
          tooltip={t("VALUE_SUB_TYPE_TOOLTIP")}
          validator="Filter.valueSubType"
        />
      );
    case "PRODUCT":
      return (
        <JarbSelect
          id={id}
          name={id}
          label={
            <>
              <span>{t("COLUMNS.VALUE_SUB_TYPE")}</span>
              <InfoTooltip tooltip={t("VALUE_SUB_TYPE_TOOLTIP")} />
            </>
          }
          placeholder={t("VALUE_SUB_TYPE_PLACEHOLDER")}
          options={PRODUCT_TYPES}
          labelForOption={(type) => type}
          jarb={{
            validator: "Filter.valueSubType",
            label: t("COLUMNS.VALUE_SUB_TYPE")
          }}
        />
      );
    default:
      return <></>;
  }
}
